import React, { useState, useEffect, useRef } from 'react';
// Multi-Select Dropdown Component
const MultiSelectDropdown = ({
    options = [],
    selectedValues = [],
    onChange,
    placeholder = "Select options...",
    disabled = false,
    name = ""
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    // Filter options based on search term
    const filteredOptions = options.filter(option =>
        option.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Handle clicking outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen]);

    const handleOptionClick = (option) => {
        let newSelectedValues;
        if (selectedValues.includes(option)) {
            newSelectedValues = selectedValues.filter(val => val !== option);
        } else {
            newSelectedValues = [...selectedValues, option];
        }
        onChange(newSelectedValues);
    };

    const removeSelectedItem = (item, e) => {
        e.stopPropagation();
        const newSelectedValues = selectedValues.filter(val => val !== item);
        onChange(newSelectedValues);
    };

    const toggleDropdown = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
            setSearchTerm('');
        }
    };

    return (
        <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
            <div
                onClick={toggleDropdown}
                style={{
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    minHeight: '40px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    width: '100%',
                    gap: '4px',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    backgroundColor: disabled ? '#f5f5f5' : 'white',
                    opacity: disabled ? 0.6 : 1
                }}
            >
                {selectedValues.length > 0 ? (
                    selectedValues.map((item, index) => (
                        <span
                            key={index}
                            style={{
                                backgroundColor: '#e3f2fd',
                                color: '#1976d2',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            {item}
                            <span
                                onClick={(e) => removeSelectedItem(item, e)}
                                style={{
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                    color: '#1976d2',
                                    fontSize: '14px'
                                }}
                            >
                                ×
                            </span>
                        </span>
                    ))
                ) : (
                    <span style={{ color: '#999', fontSize: '14px' }}>{placeholder}</span>
                )}

                <span style={{ marginLeft: 'auto', color: '#666' }}>
                    {isOpen ? '▲' : '▼'}
                </span>
            </div>

            {isOpen && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    width: '100%',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    maxHeight: '200px',
                    overflow: 'hidden'
                }}>
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search..."
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: 'none',
                            borderBottom: '1px solid #eee',
                            outline: 'none',
                            fontSize: '14px'
                        }}
                    />

                    <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option, index) => (
                                <div
                                    key={index}
                                    onClick={() => handleOptionClick(option)}
                                    style={{
                                        padding: '8px 12px',
                                        cursor: 'pointer',
                                        backgroundColor: selectedValues.includes(option) ? '#f0f8ff' : 'white',
                                        color: selectedValues.includes(option) ? '#1976d2' : 'black',
                                        fontSize: '14px',
                                        borderBottom: '1px solid #f5f5f5'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!selectedValues.includes(option)) {
                                            e.target.style.backgroundColor = '#f9f9f9';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!selectedValues.includes(option)) {
                                            e.target.style.backgroundColor = 'white';
                                        }
                                    }}
                                >
                                    {option}
                                </div>
                            ))
                        ) : (
                            <div style={{
                                padding: '8px 12px',
                                color: '#999',
                                fontSize: '14px',
                                textAlign: 'center',
                                width: '80%',

                            }}>
                                No options found
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Main DepartmentSkillsSelector Component
const DepartmentSkillsSelector = ({
    department = '',
    skillsKnown = [],
    onDepartmentChange,
    onSkillsChange,
    showToast = null,
    className = '',
    departmentLabel = 'Department',
    skillsLabel = 'Skills Known',
    departmentRequired = true,
    skillsRequired = true,
    departmentPlaceholder = 'Select Department...',
    skillsPlaceholder = 'Select Skills Known...',
    layout = 'horizontal',// 'horizontal' or 'vertical'
    readOnly = false // ✅ Add this new prop

}) => {
    const [departments, setDepartments] = useState([]);
    const [availableSkills, setAvailableSkills] = useState([]);
    const [allSkillsData, setAllSkillsData] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fetch skills data on component mount
    useEffect(() => {
        fetchSkillsData();
    }, []);

    // Update available skills when department changes
    useEffect(() => {
        if (!department) {
            setAvailableSkills([]);
            return;
        }

        const departmentSkills = [...new Set(
            allSkillsData
                .filter(item => item.Department === department)
                .map(item => item.skillKnown)
                .filter(Boolean)
        )];
        setAvailableSkills(departmentSkills);
    }, [department, allSkillsData]);

    // 2️⃣ Ensure selected skills are valid
    useEffect(() => {
        if (!department || !availableSkills.length) return;

        const validSkills = skillsKnown.filter(skill => availableSkills.includes(skill));
        if (validSkills.length !== skillsKnown.length) {
            onSkillsChange(validSkills);
        }
    }, [department, availableSkills]);

    const fetchSkillsData = async () => {
        try {
            const token = localStorage.getItem('authToken') || localStorage.getItem('token');

            console.log('Fetching skills data...');

            const response = await fetch(`${process.env.REACT_APP_API_URL}/alltech`, {
                method: 'GET',
                headers: {
                    ...(token && { 'Authorization': `Bearer ${token}` }),
                    'Content-Type': 'application/json'
                }
            });

            console.log('Skills API response status:', response.status);

            if (response.ok) {
                const result = await response.json();
                console.log('Skills API response:', result);

                if (result.success && result.data && Array.isArray(result.data)) {
                    setAllSkillsData(result.data);

                    // Extract unique departments
                    const uniqueDepartments = [...new Set(result.data.map(item => item.Department))].filter(Boolean);
                    console.log('Unique departments:', uniqueDepartments);
                    setDepartments(uniqueDepartments);
                } else {
                    console.error('Unexpected API response format:', result);
                    if (showToast) {
                        showToast('Failed to load skills data - unexpected format.', 'error');
                    }
                }
            } else {
                const errorText = await response.text();
                console.error('Failed to fetch skills data:', response.status, errorText);
                if (showToast) {
                    showToast('Failed to load skills data.', 'error');
                }
            }
        } catch (error) {
            console.error('Error fetching skills data:', error);
            if (showToast) {
                showToast('Error loading skills data.', 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDepartmentChange = (e) => {
        const selectedDepartment = e.target.value;
        if (onDepartmentChange) {
            onDepartmentChange(selectedDepartment);
        }
    };

    const handleSkillsChange = (selectedSkills) => {
        if (onSkillsChange) {
            onSkillsChange(selectedSkills);
        }
    };

    if (loading) {
        return (
            <div className={`department-skills-loading ${className}`}>
                <div>Loading departments and skills...</div>
            </div>
        );
    }

    const containerClass = layout === 'vertical' ? 'department-skills-vertical' : 'form-row';

    return (
        <div className={`department-skills-selector ${className}`}>
            <div className={containerClass}>
                <div className="form-group">
                    <label
                        style={{ fontSize: '12px', fontWeight: 600, color: '#111', marginBottom: '4px', textAlign: 'center' }}  >
                        {departmentLabel} {departmentRequired && <span className="mandatory">*</span>}
                    </label>
                    <select
                        name="department"
                        value={department}
                        onChange={!readOnly ? handleDepartmentChange : undefined}
                        required={departmentRequired}
                        disabled={readOnly}
                        style={{ backgroundColor: '#f5f5f5', color: 'black', cursor: 'not-allowed' }}
                    >

                        <option value="">{departmentPlaceholder}</option>
                        {departments.map((dept, index) => (
                            <option key={index} value={dept}>
                                {dept}
                            </option>
                        ))}
                    </select>
                </div>

             <div className="form-group">
                    <label style={{ fontSize: '14px', fontWeight: 600, color: '#111', marginBottom: '4px', textAlign: 'center' }}>
                        {skillsLabel} {skillsRequired && <span className="mandatory">*</span>}
                    </label>
                    <MultiSelectDropdown
                        options={availableSkills}
                        selectedValues={skillsKnown}
                        onChange={!readOnly ? handleSkillsChange : () => { }}
                        placeholder={department ? skillsPlaceholder : "Select Department first"}
                        disabled={readOnly || !department}

                        name="skillKnown"
                    />
                </div>
            </div>
        </div>
    );
};

export default DepartmentSkillsSelector;